import React from 'react';
import { t } from '../../translation';
import { ObserverData, SetProperty } from '../../types';
import CollapsibleSection from './collapsible-section';
import { LanguageSelector } from './language-selector';
import QualitySelector from './quality-selector';
import CameraSliders from './camera-sliders';
import PositionsMenu from './positions-menu';
import DamageToggle from './damage-toggle';

interface RightPanelProps {
    observerData: ObserverData;
    setProperty: SetProperty;
    requestId: string;
    hasSceneLoaded: boolean;
    cameraPositionsVisible: boolean;
    onToggleCameraPositions: () => void;
}

const RightPanel: React.FC<RightPanelProps> = ({
    observerData,
    setProperty,
    requestId,
    hasSceneLoaded,
    cameraPositionsVisible,
    onToggleCameraPositions
}) => {
    const [gearOpen, setGearOpen] = React.useState(false);
    const [damageOpen, setDamageOpen] = React.useState(false);
    const [positionsOpen, setPositionsOpen] = React.useState(false);
    const [orbitalEnabled, setOrbitalEnabled] = React.useState(true);
    const [navMapOpenedOnce, setNavMapOpenedOnce] = React.useState(false);

    React.useEffect(() => {
        if (cameraPositionsVisible && !navMapOpenedOnce) setNavMapOpenedOnce(true);
    }, [cameraPositionsVisible, navMapOpenedOnce]);

    React.useEffect(() => {
        const sync = (): boolean => {
            if (!window.viewer) return false;
            window.viewer.setOrbitalControlsEnabled?.(true);
            setOrbitalEnabled(true);
            return true;
        };
        if (!sync()) {
            const id = window.setInterval(() => { if (sync()) window.clearInterval(id); }, 200);
            return () => window.clearInterval(id);
        }
    }, []);

    React.useEffect(() => {
        const handler = (event: Event) => {
            const ce = event as CustomEvent<{ enabled?: boolean }>;
            setOrbitalEnabled(typeof ce?.detail?.enabled === 'boolean' ? ce.detail.enabled : Boolean(window.viewer?.enableOrbitalControls));
        };
        window.addEventListener('orbital-controls-changed', handler);
        return () => window.removeEventListener('orbital-controls-changed', handler);
    }, []);

    const handleOrbitalClick = () => {
        const v = !orbitalEnabled;
        setOrbitalEnabled(v);
        window.viewer?.setOrbitalControlsEnabled?.(v);
    };

    const handleScreenshot = () => {
        if (!window.viewer) return;
        try {
            window.viewer.renderNextFrame();
            setTimeout(() => { window.viewer.downloadPngScreenshot(); }, 100);
        } catch (e) {
            console.error('Error taking screenshot:', e);
            alert(t('error.screenshot'));
        }
    };

    return (
        <div className="right-panel" onWheel={(e) => e.stopPropagation()}>
            {/* Toolbar: horizontal icon buttons */}
            <div className="toolbar-row">
                {/* Orbital */}
                <button type="button" className={`toolbar-btn${orbitalEnabled ? ' toolbar-btn--active' : ''}`} onClick={handleOrbitalClick} title={t('camera.orbitalMode')} aria-label={t('camera.orbitalMode')}>
                    <svg className="toolbar-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="2" stroke="currentColor" strokeWidth="1.5"/>
                        <line x1="12" y1="2" x2="12" y2="8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        <line x1="12" y1="16" x2="12" y2="22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        <line x1="2" y1="12" x2="8" y2="12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        <line x1="16" y1="12" x2="22" y2="12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        <line x1="5.64" y1="5.64" x2="9.17" y2="9.17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        <line x1="14.83" y1="14.83" x2="18.36" y2="18.36" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                </button>

                {/* NavMap */}
                <button type="button" className={`toolbar-btn${cameraPositionsVisible ? ' toolbar-btn--active' : ''}`} onClick={onToggleCameraPositions} title={t('ui.augmentedNavMap')} aria-label={t('ui.augmentedNavMap')}>
                    <svg className="toolbar-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 7l6-3 6 3 6-3v13l-6 3-6-3-6 3V7z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                        <line x1="9" y1="4" x2="9" y2="17" stroke="currentColor" strokeWidth="1.5"/>
                        <line x1="15" y1="7" x2="15" y2="20" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                </button>

                {/* Damages */}
                {navMapOpenedOnce && (
                    <button type="button" className={`toolbar-btn${damageOpen ? ' toolbar-btn--active' : ''}`} onClick={() => setDamageOpen(!damageOpen)} title={t('ui.damageToggle')} aria-label={t('ui.damageToggle')}>
                        <svg className="toolbar-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="10" cy="10" r="6" stroke="currentColor" strokeWidth="1.5"/>
                            <line x1="14.5" y1="14.5" x2="21" y2="21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            <line x1="7" y1="8" x2="13" y2="12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            <line x1="8" y1="12" x2="12" y2="8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                    </button>
                )}

                {/* Screenshot */}
                <button type="button" className="toolbar-btn" onClick={handleScreenshot} title={t('app.takeScreenshot')} aria-label={t('app.takeScreenshot')}>
                    <svg className="toolbar-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2v11z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                        <circle cx="12" cy="13" r="4" stroke="currentColor" strokeWidth="1.5"/>
                        <line x1="17" y1="1" x2="19" y2="4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        <line x1="15" y1="1" x2="17" y2="4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                </button>

                {/* Settings - rightmost */}
                <button type="button" className={`toolbar-btn${gearOpen ? ' toolbar-btn--open' : ''}`} onClick={() => setGearOpen(!gearOpen)} title={t('ui.gearTooltip')} aria-label={t('ui.gearTooltip')}>
                    <svg className="toolbar-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 15.5A3.5 3.5 0 1 0 12 8.5a3.5 3.5 0 0 0 0 7z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M19.14 15.17a1.5 1.5 0 0 0 .3 1.65l.05.06a1.82 1.82 0 0 1-1.29 3.1 1.82 1.82 0 0 1-1.29-.53l-.06-.06a1.5 1.5 0 0 0-1.65-.3 1.5 1.5 0 0 0-.91 1.37v.17a1.82 1.82 0 1 1-3.64 0v-.09A1.5 1.5 0 0 0 9.67 19.14a1.5 1.5 0 0 0-1.65.3l-.06.05a1.82 1.82 0 0 1-2.57-2.57l.06-.06a1.5 1.5 0 0 0 .3-1.65 1.5 1.5 0 0 0-1.37-.91h-.17a1.82 1.82 0 1 1 0-3.64h.09A1.5 1.5 0 0 0 5.86 9.67a1.5 1.5 0 0 0-.3-1.65l-.05-.06a1.82 1.82 0 1 1 2.57-2.57l.06.06a1.5 1.5 0 0 0 1.65.3h.07a1.5 1.5 0 0 0 .91-1.37v-.17a1.82 1.82 0 1 1 3.64 0v.09a1.5 1.5 0 0 0 .91 1.37 1.5 1.5 0 0 0 1.65-.3l.06-.05a1.82 1.82 0 0 1 2.57 2.57l-.06.06a1.5 1.5 0 0 0-.3 1.65v.07a1.5 1.5 0 0 0 1.37.91h.17a1.82 1.82 0 0 1 0 3.64h-.09a1.5 1.5 0 0 0-1.37.91z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
            </div>

            {/* Dropdown areas - independent, can be open simultaneously */}
            <CollapsibleSection isOpen={gearOpen}>
                <div className="gear-dropdown-content">
                    <LanguageSelector />
                    <QualitySelector />
                </div>
            </CollapsibleSection>

            <CollapsibleSection isOpen={!orbitalEnabled}>
                <CameraSliders />
                <PositionsMenu
                    isOpen={positionsOpen}
                    onToggle={() => setPositionsOpen(!positionsOpen)}
                    observerData={observerData}
                    setProperty={setProperty}
                />
            </CollapsibleSection>

            <CollapsibleSection isOpen={damageOpen}>
                <DamageToggle visible={true} skipToggle={true} isActive={damageOpen} />
            </CollapsibleSection>
        </div>
    );
};

export default RightPanel;
